import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { site } from "@/config/site";
import { WhatsAppIcon } from "./CtaButtons";

const Dropdown = ({ label, to, items, testId }) => (
  <div className="relative group">
    <Link to={to} className="nav-link inline-flex items-center gap-1 py-2" data-testid={testId}>
      {label} <ChevronDown size={14} aria-hidden="true" className="transition-transform group-hover:rotate-180" />
    </Link>
    <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200">
      <ul className="min-w-[230px] rounded-2xl bg-white shadow-xl border border-black/10 p-2">
        {items.map((it) => (
          <li key={it.path}>
            <Link to={it.path} className="block rounded-xl px-4 py-2.5 text-sm text-ink/80 hover:bg-emerald/5 hover:text-emerald transition-colors">
              {it.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [shrunk, setShrunk] = useState(false);
  useEffect(() => {
    const onScroll = () => setShrunk(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const close = () => setOpen(false);
  const serviceItems = site.services.map((s) => ({ label: s.name, path: s.path }));

  return (
    <header className="sticky top-0 z-40 w-full bg-night/95 backdrop-blur border-b border-gold/20" data-testid="site-header">
      <div className={`container-wide flex items-center justify-between transition-[height] duration-300 ${shrunk ? "h-16 lg:h-[68px]" : "h-[72px] lg:h-[88px]"}`}>
        <Link to="/" className="flex items-center py-1 group" onClick={close} data-testid="header-logo">
          <img
            src="/images/lavenza-spa-logo-header.png"
            alt="Lavenza Spa Bursa logo"
            width="822"
            height="620"
            fetchPriority="high"
            className={`logo-glow w-auto transition-[height,filter] duration-300 group-hover:brightness-110 ${shrunk ? "h-10 lg:h-[52px]" : "h-12 lg:h-[72px]"}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Ana menü" data-testid="desktop-nav">
          <NavLink to="/" end className="nav-link">Ana Sayfa</NavLink>
          <Dropdown label="Hizmetler" to="/masaj-hizmetleri" items={serviceItems} testId="nav-services" />
          <Dropdown label="Bölgeler" to="/bursa-masaj-salonu" items={site.regions} testId="nav-regions" />
          <NavLink to="/hakkimizda" className="nav-link">Hakkımızda</NavLink>
          <NavLink to="/iletisim" className="nav-link">İletişim</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={site.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp ile randevu"
            className="hidden md:flex h-11 w-11 items-center justify-center rounded-full bg-whatsapp text-white hover:bg-[#1eb558] transition-colors shadow-md"
            data-testid="header-whatsapp"
          >
            <WhatsAppIcon />
          </a>
          <div className="hidden md:block">
            <a href={site.phoneTel} className="btn btn-gold btn-sm" data-testid="header-call">
              <Phone size={16} aria-hidden="true" /> {site.phoneShort}
            </a>
          </div>
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-gold hover:bg-white/10"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Menüyü kapat" : "Menüyü aç"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <nav id="mobile-menu" className="lg:hidden border-t border-gold/20 bg-night text-white" aria-label="Mobil menü" data-testid="mobile-menu">
          <ul className="container-wide py-4 flex flex-col gap-1 text-base">
            <li><Link to="/" onClick={close} className="block py-2.5 font-medium">Ana Sayfa</Link></li>
            <li><Link to="/masaj-hizmetleri" onClick={close} className="block py-2.5 font-medium">Hizmetler</Link>
              <ul className="pl-4 border-l border-gold/40 ml-1">
                {site.services.map((s) => <li key={s.slug}><Link to={s.path} onClick={close} className="block py-2 text-sm text-white/60">{s.name}</Link></li>)}
              </ul>
            </li>
            <li><span className="block py-2.5 font-medium">Bölgeler</span>
              <ul className="pl-4 border-l border-gold/40 ml-1">
                {site.regions.map((r) => <li key={r.path}><Link to={r.path} onClick={close} className="block py-2 text-sm text-white/60">{r.label}</Link></li>)}
              </ul>
            </li>
            <li><Link to="/hakkimizda" onClick={close} className="block py-2.5 font-medium">Hakkımızda</Link></li>
            <li><Link to="/iletisim" onClick={close} className="block py-2.5 font-medium">İletişim</Link></li>
          </ul>
        </nav>
      )}
    </header>
  );
};
